import { idadeEmMeses, versaoAtual } from '@equiview/domain';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useUrlDaMidia } from '../components/FotoDaMidia.js';
import { PlayerDeMarcha } from '../components/PlayerDeMarcha.js';
import { IconeCamera, IconeVideo } from '../components/Icones.js';
import { Aguardar, Botao, BotaoLink, CabecalhoPagina, Dado, EstadoVazio, Painel } from '../components/ui/index.js';
import { formatarData, formatarIdade, ROTULO_ANDAMENTO, ROTULO_VISTA, tituloDaMidia } from '../dados/catalogo.js';
import { useConsulta, useFonte } from '../dados/contexto.js';
import { podeAnalisar, type AnaliseDeMidia, type Animal, type Avaliacao, type Midia, type Usuario } from '../dados/fonte.js';

/** Foto ou video de uma ficha, com a analise da equipe (versionada, nunca sobrescrita). */
export function PaginaMidia() {
  const { id = '', midiaId = '' } = useParams();
  const consulta = useConsulta(
    async (f) => {
      const [animal, midia, usuario] = await Promise.all([f.animal(id), f.midia(midiaId), f.usuarioAtual()]);
      const avaliacoes = await f.avaliacoes(id);
      const avaliacao = avaliacoes.find((a) => a.id === midia?.assessment_id) ?? null;
      const irmas = avaliacao ? await f.midias(avaliacao.id) : [];
      return { animal, midia, usuario, avaliacao, irmas };
    },
    [id, midiaId],
  );

  return (
    <Aguardar consulta={consulta}>
      {({ animal, midia, avaliacao, ...resto }) =>
        animal && midia && avaliacao ? (
          <Visualizador animal={animal} midia={midia} avaliacao={avaliacao} {...resto} />
        ) : (
          <EstadoVazio titulo="Mídia não encontrada" acao={<BotaoLink to={`/animais/${id}`}>Voltar à ficha</BotaoLink>} />
        )
      }
    </Aguardar>
  );
}

function Visualizador({
  animal,
  midia,
  avaliacao,
  irmas,
  usuario,
}: {
  animal: Animal;
  midia: Midia;
  avaliacao: Avaliacao;
  irmas: Midia[];
  usuario: Usuario | null;
}) {
  const navegar = useNavigate();
  const indice = irmas.findIndex((m) => m.id === midia.id);
  const anterior = irmas[indice - 1];
  const proxima = irmas[indice + 1];
  const base = `/animais/${animal.id}`;
  const fichaUrl = `${base}?ficha=${avaliacao.id}`;

  // Setas do teclado percorrem as midias da ficha (fora de campos de texto).
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest('input, textarea, select, [contenteditable]')) return;
      if (e.key === 'ArrowLeft' && anterior) navegar(`${base}/midia/${anterior.id}`);
      if (e.key === 'ArrowRight' && proxima) navegar(`${base}/midia/${proxima.id}`);
    };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [anterior, proxima, base, navegar]);

  return (
    <div className="space-y-6">
      <CabecalhoPagina
        trilha={[
          { rotulo: 'Plantel', para: '/animais' },
          { rotulo: animal.name, para: fichaUrl },
          { rotulo: tituloDaMidia(midia) },
        ]}
        sobretitulo={`Avaliação de ${formatarData(avaliacao.visit_date)} · aos ${formatarIdade(idadeEmMeses(animal.birth_date, avaliacao.visit_date))}`}
        titulo={tituloDaMidia(midia)}
        meta={<span>{animal.name}</span>}
      />

      {/* minmax(0, ...) tambem no celular: sem isso a coluna cresce ate a faixa de miniaturas e a pagina vaza. */}
      <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="space-y-3">
          <div className="overflow-hidden rounded-(--radius-painel) border border-borda">
            <Arquivo midia={midia} />
            <div className="flex items-center justify-between gap-3 bg-abismo px-3 py-2 text-nevoa">
              {anterior ? (
                <Link
                  to={`${base}/midia/${anterior.id}`}
                  aria-label={`Anterior: ${tituloDaMidia(anterior)}`}
                  className="alvo-toque px-2 py-1 text-sm hover:text-gelo"
                >
                  ← Anterior
                </Link>
              ) : (
                <span />
              )}
              <span className="numero text-xs tracking-wide uppercase">
                {indice + 1} de {irmas.length}
                <span className="hidden sm:inline"> · use ← →</span>
              </span>
              {proxima ? (
                <Link
                  to={`${base}/midia/${proxima.id}`}
                  aria-label={`Próxima: ${tituloDaMidia(proxima)}`}
                  className="alvo-toque px-2 py-1 text-sm hover:text-gelo"
                >
                  Próxima →
                </Link>
              ) : (
                <span />
              )}
            </div>
          </div>

          <nav aria-label="Outras mídias desta avaliação" className="overflow-x-auto">
            <ol className="flex gap-2">
              {irmas.map((m) => {
                const atual = m.id === midia.id;
                const Icone = m.kind === 'video' ? IconeVideo : IconeCamera;
                return (
                  <li key={m.id} className="shrink-0">
                    <Link
                      to={`${base}/midia/${m.id}`}
                      aria-current={atual ? 'page' : undefined}
                      title={tituloDaMidia(m)}
                      className={`flex h-16 w-28 flex-col items-center justify-center gap-1 border-2 bg-abismo px-1 text-center text-nevoa transition-colors ${
                        atual ? 'border-destaque' : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                    >
                      <Icone width={18} height={18} />
                      <span className="line-clamp-1 text-xs leading-tight">
                        {m.view ? ROTULO_VISTA[m.view] : ''}
                        {m.gait ? ` · ${ROTULO_ANDAMENTO[m.gait]}` : ''}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ol>
          </nav>

          <Painel
            titulo="Dados da mídia"
            acoes={
              midia.kind === 'foto' &&
              midia.view === 'perfil_esquerdo' &&
              podeAnalisar(usuario) && (
                <BotaoLink tamanho="sm" to={`/animais/${animal.id}/midia/${midia.id}/angulos`}>
                  Medir ângulos na foto
                </BotaoLink>
              )
            }
          >
            <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
              <Dado rotulo="Tipo">{midia.kind === 'foto' ? 'Foto' : 'Vídeo'}</Dado>
              <Dado rotulo="Vista">{midia.view ? ROTULO_VISTA[midia.view] : '—'}</Dado>
              <Dado rotulo="Andamento">{midia.gait ? ROTULO_ANDAMENTO[midia.gait] : '—'}</Dado>
              <Dado rotulo="Arquivo">{midia.storage_path ? 'Enviado' : midia.pendente ? 'Neste aparelho, aguardando envio' : 'Não enviado'}</Dado>
            </dl>
          </Painel>
        </div>

        {/* key: trocar de midia recomeca o formulario, sem carregar rascunho da anterior. */}
        <Analise key={midia.id} midiaId={midia.id} usuario={usuario} />
      </div>
    </div>
  );
}

/** A imagem ou o video, sobre Abismo. Sem arquivo disponivel, diz por que — nunca mostra uma imagem inventada. */
function Arquivo({ midia }: { midia: Midia }) {
  const url = useUrlDaMidia(midia);
  if (url) {
    return midia.kind === 'video' ? (
      <PlayerDeMarcha url={url} titulo={tituloDaMidia(midia)} />
    ) : (
      <img src={url} alt={tituloDaMidia(midia)} className="w-full bg-abismo" />
    );
  }
  const Icone = midia.kind === 'video' ? IconeVideo : IconeCamera;
  const [titulo, texto] = midia.storage_path
    ? ['Sem conexão para abrir o arquivo', 'O arquivo está no servidor e abre quando houver internet.']
    : [
        'Arquivo ainda não enviado',
        `${midia.kind === 'video' ? 'O vídeo' : 'A foto'} aparece aqui quando for enviado ao armazenamento. A análise já pode ser escrita.`,
      ];
  return (
    <div className="flex aspect-video flex-col items-center justify-center gap-2 bg-abismo px-6 text-center text-nevoa">
      <Icone width={44} height={44} />
      <p className="font-medium text-gelo">{titulo}</p>
      <p className="max-w-sm text-sm text-nevoa/80">{texto}</p>
    </div>
  );
}

const formatarMomento = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

function Analise({ midiaId, usuario }: { midiaId: string; usuario: Usuario | null }) {
  const fonte = useFonte();
  const [versoes, setVersoes] = useState<AnaliseDeMidia[] | null>(null);
  const [texto, setTexto] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    let vivo = true;
    void fonte.analises(midiaId).then((v) => {
      if (!vivo) return;
      setVersoes(v);
      setTexto(versaoAtual(v)?.body ?? '');
    });
    return () => {
      vivo = false;
    };
  }, [fonte, midiaId]);

  const atual = versoes ? versaoAtual(versoes) : null;
  const mudou = texto.trim() !== (atual?.body ?? '') && texto.trim() !== '';

  // Texto escrito e nao salvo: o navegador pergunta antes de sair.
  useEffect(() => {
    if (!mudou) return;
    const aviso = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', aviso);
    return () => window.removeEventListener('beforeunload', aviso);
  }, [mudou]);

  if (!versoes) return <div className="esqueleto h-80" />;

  const anteriores = versoes.filter((v) => v.id !== atual?.id);
  const editavel = podeAnalisar(usuario);

  const salvar = async () => {
    if (!mudou || salvando) return;
    setSalvando(true);
    setErro(null);
    try {
      const nova = await fonte.salvarAnalise({ media_id: midiaId, body: texto, supersedes: atual?.id ?? null });
      setVersoes([nova, ...versoes]);
      setTexto(nova.body);
      setSalvo(true);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <section aria-label="Análise" className="lg:sticky lg:top-18">
      <Painel
        titulo="Análise"
        acoes={
          atual && (
            <span className="text-xs text-apagado">
              {atual.author_name} · {formatarMomento(atual.created_at)}
            </span>
          )
        }
      >
        <div className="space-y-4">
          {editavel ? (
            <>
              <label className="block space-y-1.5">
                <span className="sr-only">Texto da análise</span>
                <textarea
                  value={texto}
                  onChange={(e) => {
                    setTexto(e.target.value);
                    setSalvo(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      void salvar();
                    }
                  }}
                  rows={10}
                  placeholder="Escreva a análise desta foto ou vídeo: o que se observa, pontos fortes, defeitos, o que reavaliar."
                  className="w-full resize-y rounded-(--radius-controle) border border-borda-forte bg-superficie p-3 text-[15px] leading-relaxed outline-none placeholder:text-apagado focus:border-destaque"
                />
                <span className="flex justify-between text-xs text-apagado">
                  <span>Ctrl + Enter salva</span>
                  <span className="numero">{texto.trim().length} caracteres</span>
                </span>
              </label>
              {erro && (
                <p role="alert" className="border-l-2 border-erro bg-erro/8 px-3 py-2 text-sm text-erro">
                  {erro}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-3">
                <Botao variante="principal" onClick={salvar} disabled={!mudou} carregando={salvando}>
                  {salvando ? 'Salvando…' : atual ? 'Salvar nova versão' : 'Salvar análise'}
                </Botao>
                <span className="text-xs text-apagado" aria-live="polite">
                  {salvo ? 'Salvo.' : mudou ? 'Alterações não salvas.' : atual ? 'A versão atual fica guardada no histórico.' : ''}
                </span>
              </div>
            </>
          ) : atual ? (
            <p className="leading-relaxed whitespace-pre-wrap">{atual.body}</p>
          ) : (
            <p className="text-sm text-apagado">Ainda sem análise.</p>
          )}

          {anteriores.length > 0 && (
            <details className="border-t border-borda pt-3">
              <summary className="flex min-h-9 cursor-pointer items-center text-sm font-medium text-destaque-texto">
                Histórico · {anteriores.length} {anteriores.length === 1 ? 'versão anterior' : 'versões anteriores'}
              </summary>
              <ol className="mt-3 space-y-4 border-l border-borda-forte pl-4">
                {anteriores.map((v) => (
                  <li key={v.id} className="relative">
                    <span aria-hidden className="absolute top-1.5 -left-5.25 size-2 rotate-45 bg-borda-forte" />
                    <p className="text-xs text-apagado">
                      {v.author_name} · {formatarMomento(v.created_at)}
                      {v.deleted_at && ' · apagada'}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed whitespace-pre-wrap">{v.body}</p>
                  </li>
                ))}
              </ol>
            </details>
          )}
        </div>
      </Painel>
    </section>
  );
}
